import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDraft } from '../contexts/DraftContext';
import DraftControls from '../components/DraftControls';
import AvailablePlayers from '../components/AvailablePlayers';
import TeamRoster from '../components/TeamRoster';

/**
 * Mobile-first participant screen for Commissioner Mode drafts -- swapped in by DraftView.jsx
 * instead of the desktop three-panel DraftBoardLayout whenever draft.commissionerMode is true.
 * Solo Draft Mode and Mock Draft keep DraftBoardLayout completely unchanged.
 *
 * Deliberately a new layout, not new pick/roster/AI logic: DraftControls (turn status, pick
 * timer, commissioner skip/extend, D1/D3 AI buttons, undo, leave), AvailablePlayers (the pick
 * flow itself), and TeamRoster (the viewer's own roster) are already self-contained via
 * useDraft() and already Commissioner-Mode-aware (canPick/isUserTurn resolve through
 * mySlotIndex, PickTimer only renders when commissionerMode is set, "Create League" is already
 * hidden for these drafts) -- reusing them directly means zero duplicated pick/turn/AI logic and
 * zero risk to solo Draft Mode/Mock Draft's DraftBoardLayout, which stays byte-for-byte
 * untouched. The one genuinely new piece here is a compact recent-picks strip with a link out to
 * the full board (DraftTvView, at /draft/:draftId/tv) in place of the desktop DraftBoard grid.
 */
function CommissionerDraftView() {
  const { draft, picks, error, clearError } = useDraft();
  const [rosterOpen, setRosterOpen] = useState(false);
  const navigate = useNavigate();

  if (!draft) return null;

  const recentPicks = [...picks]
    .sort((a, b) => (a.overallPick ?? 0) - (b.overallPick ?? 0))
    .slice(-5)
    .reverse();

  return (
    <div className='commissioner-draft-view'>
      <DraftControls />

      {error && (
        <div className='error-banner error-banner--dismissable' style={{ margin: 0 }}>
          {error}
          <button className='error-banner__dismiss' onClick={clearError} aria-label='Dismiss'>✕</button>
        </div>
      )}

      <AvailablePlayers />

      <details
        className='commissioner-roster-details'
        open={rosterOpen}
        onToggle={e => setRosterOpen(e.target.open)}
      >
        <summary>Your Roster</summary>
        <TeamRoster />
      </details>

      <div className='commissioner-recent-picks'>
        <h3 className='panel-title'>Recent Picks</h3>
        {recentPicks.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No picks yet.</p>
        ) : (
          <ul className='commissioner-recent-picks-list'>
            {recentPicks.map((p, i) => (
              <li key={i}>
                <span className={`pos-badge pos-${p.position}`}>{p.position}</span>
                {' '}{p.playerName}{' '}
                <span style={{ color: 'var(--text-muted)' }}>({p.team})</span>
              </li>
            ))}
          </ul>
        )}
        <button
          type='button'
          className='btn btn-secondary'
          onClick={() => navigate(`/draft/${draft.draftId}/tv`)}
        >
          View Full Board
        </button>
      </div>
    </div>
  );
}

export default CommissionerDraftView;